import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi'
import MetricCard from '@/components/ui/MetricCard'
import { ROUTES } from '@/constants/routes'
import PurchasesDetailsModal from './PurchasesDetailsModal'
import PdfViewerModal from './PdfViewerModal'
import { getPurchases, getPurchasesSummary, getPurchaseBillPdfUrl, purchaseToTableRow } from '@/services/api/finance'
import PageSizeSelect from '@/components/common/PageSizeSelect'

type PurchaseRow = ReturnType<typeof purchaseToTableRow>

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

interface FinancePurchasesProps {
  /** When true, hide the page header (title, breadcrumb, Finance tabs, This Month) for embedding in Reports. */
  embeddedInReports?: boolean
}

const FinancePurchases = ({ embeddedInReports }: FinancePurchasesProps) => {
  const location = useLocation()
  const [timeframe, setTimeframe] = useState('This Month')
  const [anyDate, setAnyDate] = useState('Any Date')
  const [startDate, setStartDate] = useState('Start Date')
  const [endDate, setEndDate] = useState('End Date')
  const [filter, setFilter] = useState('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(25)
  const [isPurchasesDetailsModalOpen, setIsPurchasesDetailsModalOpen] = useState(false)
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseRow | null>(null)
  const [purchasesData, setPurchasesData] = useState<PurchaseRow[]>([])
  const [purchasesLoading, setPurchasesLoading] = useState(true)
  const [purchasesTotal, setPurchasesTotal] = useState(0)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summary, setSummary] = useState<{
    totalPurchases: number
    pendingPayments: number
    paidPurchases: number
    unpaidPurchases: number
  } | null>(null)
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfTitle, setPdfTitle] = useState('Bill')
  const [pdfLoading, setPdfLoading] = useState(false)
  const [billPdfLoadingId, setBillPdfLoadingId] = useState<string | null>(null)
  const [xeroBillUrl, setXeroBillUrl] = useState<string | null>(null)
  const [pdfErrorMessage, setPdfErrorMessage] = useState<string | null>(null)

  const fetchSummary = () => {
    setSummaryLoading(true)
    getPurchasesSummary()
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false))
  }

  useEffect(() => {
    fetchSummary()
  }, [])

  // Map date filter (Any Date / This Week / This Month / This Year) to API startDate/endDate
  const getDateRangeFromFilter = (): { startDate?: string; endDate?: string } => {
    if (anyDate === 'Any Date') return {}
    const now = new Date()
    const toISO = (d: Date) => d.toISOString().slice(0, 10)
    let start: Date
    // let _end: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    switch (anyDate) {
      case 'This Week': {
        const day = now.getDay()
        start = new Date(now)
        start.setDate(now.getDate() - day)
        start.setHours(0, 0, 0, 0)
        return { startDate: toISO(start), endDate: toISO(now) }
      }
      case 'This Month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        return { startDate: toISO(start), endDate: toISO(now) }
      case 'This Year':
        start = new Date(now.getFullYear(), 0, 1)
        return { startDate: toISO(start), endDate: toISO(now) }
      default:
        return {}
    }
  }

  useEffect(() => {
    const mapFilterToStatus = (f: string): string | undefined => {
      if (f === 'All') return undefined
      if (f === 'Paid') return 'paid'
      if (f === 'Pending') return 'pending'
      return undefined
    }

    const { startDate, endDate } = getDateRangeFromFilter()

    setPurchasesLoading(true)
    getPurchases({
      page: currentPage,
      limit: resultsPerPage,
      status: mapFilterToStatus(filter),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    })
      .then(({ purchases, meta }) => {
        setPurchasesData(purchases.map(purchaseToTableRow))
        setPurchasesTotal(meta?.total ?? purchases.length)
      })
      .catch(() => setPurchasesData([]))
      .finally(() => setPurchasesLoading(false))
  }, [currentPage, filter, anyDate, resultsPerPage])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
      case 'Pending':
        return { textColor: '#EEC233', bgColor: '#FEF9EB' }
      case 'Overdue':
        return { textColor: '#E93323', bgColor: '#FEE2E2' }
      default:
        return { textColor: '#69697A', bgColor: '#F2F4F2' }
    }
  }

  const tabs = [
    { label: 'Overview', path: ROUTES.FINANCE_OVERVIEW },
    { label: 'Invoices', path: ROUTES.FINANCE_INVOICES },
    { label: 'Purchases', path: ROUTES.FINANCE_PURCHASES },
    { label: 'Products and Services', path: ROUTES.FINANCE_PRODUCTS_SERVICES },
    { label: 'Operational Costs', path: ROUTES.FINANCE_OPERATIONAL_COSTS },
    { label: 'Business Snapshot', path: ROUTES.FINANCE_SNAPSHOT },
    { label: 'Business Analytics', path: ROUTES.FINANCE_ANALYTICS },
  ]

  const totalResults = purchasesTotal
  const totalPages = Math.max(1, Math.ceil(totalResults / resultsPerPage))
  const showingCount =
    totalResults === 0
      ? 0
      : Math.min(resultsPerPage, Math.max(0, totalResults - (currentPage - 1) * resultsPerPage))
  // Page numbers to show: 1, range around current, last (e.g. 1 ... 4 5 6 ... 10)
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: number[] = [1]
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    if (start > 2) pages.push(-1) // ellipsis
    for (let p = start; p <= end; p++) if (!pages.includes(p)) pages.push(p)
    if (end < totalPages - 1) pages.push(-2) // ellipsis
    if (totalPages > 1) pages.push(totalPages)
    return pages
  }
  const pageNumbers = getPageNumbers()

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {!embeddedInReports && (
      <div>
        {/* First Row: Finance title and Breadcrumbs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Purchases
          </h1>

          {/* Breadcrumbs on the right of Finance title */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 'clamp(11px, 2.5vw, 12px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Purchases</span>
          </span>
        </div>

        {/* Second Row: Tabs and Controls on the same level */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Tabs - full width so all tabs (including Business Analytics) are visible */}
          <div
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px'
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          {/* This Month */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" style={{ height: '40px' }}>
            <div className="relative">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="px-3 sm:px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm sm:text-base"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#050521',
                  height: '40px',
                  minWidth: '120px',
                }}
              >
                <option>This Month</option>
                <option>Last Month</option>
                <option>This Year</option>
              </select>
              <FiChevronDown
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '16px',
                  height: '16px',
                  opacity: 0.8,
                  color: '#050521'
                }}
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Total Purchases"
          value={summaryLoading ? '…' : formatCurrency(summary?.totalPurchases ?? 0)}
          trend=""
          icon={
            <img
              src="/images/dollar-circle.svg"
              alt="Total"
              className="w-5 h-5"
              style={{
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(103deg) brightness(95%) contrast(85%)'
              }}
            />
          }
          iconBgColor=""
          iconBgColorCustom="#E8F7EF"
        />
        <MetricCard
          title="Pending Payment"
          value={summaryLoading ? '…' : formatCurrency(summary?.pendingPayments ?? 0)}
          trend=""
          icon={
            <img
              src="/images/empty-wallet-time.svg"
              alt="Pending"
              className="w-5 h-5"
            />
          }
          iconBgColor=""
          iconBgColorCustom="#FEF9EB"
        />
        <MetricCard
          title="Paid Purchases"
          value={summaryLoading ? '…' : formatCurrency(summary?.paidPurchases ?? 0)}
          trend=""
          icon={
            <img
              src="/images/card.svg"
              alt="Paid"
              className="w-5 h-5"
            />
          }
          iconBgColor=""
          iconBgColorCustom="#EAF1FF"
        />
        <MetricCard
          title="Unpaid Purchases"
          value={summaryLoading ? '…' : formatCurrency(summary?.unpaidPurchases ?? 0)}
          trend=""
          icon={
            <img
              src="/images/card-remove.svg"
              alt="Unpaid"
              className="w-5 h-5"
            />
          }
          iconBgColor=""
          iconBgColorCustom="#F2F4F2"
        />
      </div>

      {/* Purchases Overview Table Section */}
      <div className="bg-white rounded-lg" style={{ paddingBottom: '24px' }}>
        <div className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2 gap-4">
            <h3
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: 'clamp(13px, 3vw, 14px)',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Purchases Overview
            </h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
              {/* Start Date */}
              <div className="relative">
                <select
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-4 py-1 pt-1.5 pl-10 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#212126',
                    height: '34px',
                    minWidth: '110px',
                  }}
                >

                  <option value="Start Date" disabled>Start Date</option>
                  <option>Today</option>
                  <option>Yesterday</option>
                  <option>This Week</option>
                  <option>Last Week</option>
                  <option>This Month</option>
                  <option>Last Month</option>
                </select>
                <img
                  src="/images/calendar.svg"
                  alt="Calendar"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <FiChevronDown
                  className="absolute right-3 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 1,
                    color: '#69697A',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              </div>
              {/* End Date */}
              <div className="relative">
                <select
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-4 py-1 pl-10 pr-8 pt-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#212126',
                    height: '34px',
                    minWidth: '110px',
                  }}
                >
                  <option value="End Date" disabled>End Date</option>
                  <option>Today</option>
                  <option>Yesterday</option>
                  <option>This Week</option>
                  <option>Last Week</option>
                  <option>This Month</option>
                  <option>Last Month</option>
                </select>
                <img
                  src="/images/calendar.svg"
                  alt="Calendar"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <FiChevronDown
                  className="absolute right-3 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 1,
                    color: '#69697A',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              </div>
              {/* Any Date Dropdown */}
              <div className="relative">
                <select
                  value={anyDate}
                  onChange={(e) => {
                    setAnyDate(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="px-4 py-1 pt-1.5 pl-10 pr-8 pb-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126',
                    height: '34px',
                    minWidth: '110px',
                  }}
                >
                  <option>Any Date</option>
                  <option>This Week</option>
                  <option>This Month</option>
                  <option>This Year</option>
                </select>
                <img
                  src="/images/calendar.svg"
                  alt="Calendar"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <FiChevronDown
                  className="absolute right-3 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 1,
                    color: '#69697A',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              </div>
              {/* All Filter Dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="px-4 py-1 pr-8 pt-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126',
                    height: '34px',
                    minWidth: '70px',
                  }}
                >
                  <option>All</option>
                  <option>Pending</option>
                  <option>Paid</option>
                </select>
                <FiChevronDown
                  className="absolute right-3 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 1,
                    color: '#69697A',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className="w-full overflow-x-auto sm:overflow-visible"
          style={{
            marginBottom: '32px',
            marginLeft: '0',
            marginRight: '0',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="sm:mx-6" style={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #E9E9EA',
            width: '100%',
            minWidth: '900px',
          }}>
            <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                  {/* From */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopLeftRadius: '8px',
                    }}
                  >
                    From
                  </th>
                  {/* Status */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Status
                  </th>
                  {/* Reference */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Reference
                  </th>
                  {/* Date */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Date
                  </th>
                  {/* Due date */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Due date
                  </th>
                  {/* Paid */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Paid
                  </th>
                  {/* Due */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Due
                  </th>
                  {/* Currency */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Currency
                  </th>
                  {/* Files */}
                  <th
                    className="text-left py-3"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopRightRadius: '8px',
                    }}
                  >
                    Files
                  </th>
                </tr>
              </thead>
              <tbody>
                {purchasesLoading ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A', borderBottom: '1px solid #E9E9EA' }}>
                      Loading…
                    </td>
                  </tr>
                ) : purchasesData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-12 text-center"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#69697A',
                        borderBottom: '1px solid #E9E9EA',
                      }}
                    >
                      No purchases yet. Sync from Xero (Settings → Integrations) or add a purchase.
                    </td>
                  </tr>
                ) : (
                  purchasesData.map((purchase, index) => {
                    const statusStyle = getStatusColor(purchase.status)
                    const isLastRow = index === purchasesData.length - 1
                    return (
                      <tr
                        key={purchase.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedPurchase(purchase)
                          setIsPurchasesDetailsModalOpen(true)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedPurchase(purchase)
                            setIsPurchasesDetailsModalOpen(true)
                          }
                        }}
                        style={{
                          borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA',
                          cursor: 'pointer',
                        }}
                      >
                        {/* From */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                            ...(isLastRow && { borderBottomLeftRadius: '8px' }),
                          }}
                        >
                          {purchase.from}
                        </td>
                        {/* Status */}
                        <td
                          className="py-3"
                          style={{
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontStyle: 'normal',
                              fontSize: '12px',
                              lineHeight: '20px',
                              letterSpacing: '0px',
                              color: statusStyle.textColor,
                              backgroundColor: statusStyle.bgColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block',
                            }}
                          >
                            {purchase.status}
                          </span>
                        </td>
                        {/* Reference */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.invoiceNo}
                        </td>
                        {/* Date */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.invoiceDate}
                        </td>
                        {/* Due date */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.invoiceDueDate}
                        </td>
                        {/* Paid */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.paid}
                        </td>
                        {/* Due */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.due}
                        </td>
                        {/* Currency */}
                        <td
                          className="py-3"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                          }}
                        >
                          {purchase.currency}
                        </td>
                        {/* Files */}
                        <td
                          className="py-3"
                          style={{
                            borderLeft: 'none',
                            borderRight: 'none',
                            paddingLeft: '16px',
                            
                            ...(isLastRow && { borderBottomRightRadius: '8px' }),
                          }}
                        >
                          {purchase.fileUrl ? (
                            <button
                              type="button"
                              disabled={billPdfLoadingId === purchase.id}
                              onClick={async (e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setPdfTitle(`Bill – ${purchase.invoiceNo || purchase.from}`)
                                setXeroBillUrl(purchase.fileUrl)
                                setPdfUrl(null)
                                setPdfErrorMessage(null)
                                setIsPdfViewerOpen(true)
                                setBillPdfLoadingId(purchase.id)
                                setPdfLoading(true)
                                try {
                                  const blobUrl = await getPurchaseBillPdfUrl(purchase.id)
                                  setPdfUrl(blobUrl)
                                  setPdfErrorMessage(null)
                                } catch (err: unknown) {
                                  console.error('Failed to load bill PDF:', err)
                                  setPdfUrl(null)
                                  let message = 'Could not load the bill PDF.'
                                  const res = (err as { response?: { data?: Blob | { message?: string }; status?: number } })?.response
                                  if (res?.data) {
                                    if (res.data instanceof Blob) {
                                      try {
                                        const text = await res.data.text()
                                        const j = JSON.parse(text) as { error?: { message?: string }; message?: string }
                                        message = j?.error?.message ?? j?.message ?? message
                                      } catch {
                                        if (res.status === 404) message = 'This bill has no PDF in Xero or the link is missing.'
                                      }
                                    } else if (typeof (res.data as { message?: string }).message === 'string') {
                                      message = (res.data as { message: string }).message
                                    }
                                  }
                                  setPdfErrorMessage(message)
                                } finally {
                                  setBillPdfLoadingId(null)
                                  setPdfLoading(false)
                                }
                              }}
                              title="View bill PDF in app"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#2563EB',
                                background: 'none',
                                border: 'none',
                                padding: '6px',
                                minWidth: '28px',
                                minHeight: '28px',
                                borderRadius: '6px',
                                cursor: billPdfLoadingId === purchase.id ? 'wait' : 'pointer',
                              }}
                            >
                              {billPdfLoadingId === purchase.id ? (
                                <span style={{ fontSize: '12px' }}>…</span>
                              ) : (
                                <img
                                  src="/images/document-text.svg"
                                  alt="Bill file"
                                  style={{ width: '18px', height: '18px' }}
                                />
                              )}
                            </button>
                          ) : (
                            <span
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontSize: '12px',
                                color: '#9CA3AF',
                              }}
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={9}
                    className="py-4"
                    style={{
                      borderTop: '1px solid #E9E9EA',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingBottom: '20px',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#69697A',
                        }}
                      >
                        Showing {showingCount} of {totalResults} Results
                      </span>
                      <div className="flex items-center gap-2">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                          }}
                        >
                          <FiChevronLeft style={{ width: '16px', height: '16px' }} />
                          <span>Previous</span>
                        </button>
                        {pageNumbers.map((page) =>
                          page < 0 ? (
                            <span
                              key={page === -1 ? 'ellipsis-start' : 'ellipsis-end'}
                              className="px-2"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontSize: '14px',
                                color: '#69697A',
                              }}
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className="px-3 py-1 border rounded"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '0%',
                                color: currentPage === page ? '#FFFFFF' : '#212126',
                                backgroundColor: currentPage === page ? '#212126' : '#FFFFFF',
                                borderColor: currentPage === page ? '#212126' : '#E9E9EA',
                              }}
                            >
                              {page}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: '#212126',
                          }}
                        >
                          <span>Next</span>
                          <FiChevronRight style={{ width: '16px', height: '16px' }} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Purchases Details Modal */}
      <PurchasesDetailsModal
        isOpen={isPurchasesDetailsModalOpen}
        onClose={() => setIsPurchasesDetailsModalOpen(false)}
        purchaseData={selectedPurchase ? {
          from: selectedPurchase.from,
          invoiceNo: selectedPurchase.invoiceNo,
          invoiceDate: selectedPurchase.invoiceDate,
          invoiceDueDate: selectedPurchase.invoiceDueDate,
          paid: selectedPurchase.paid,
          due: selectedPurchase.due,
          currency: selectedPurchase.currency,
          status: selectedPurchase.status,
        } : undefined}
      />

      {/* Bill PDF viewer modal – show bill in-app like Xero */}
      <PdfViewerModal
        isOpen={isPdfViewerOpen}
        onClose={() => {
          if (pdfUrl && pdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(pdfUrl)
          }
          setIsPdfViewerOpen(false)
          setPdfUrl(null)
          setPdfTitle('Bill')
          setXeroBillUrl(null)
          setPdfErrorMessage(null)
        }}
        pdfUrl={pdfUrl}
        title={pdfTitle}
        isLoading={pdfLoading}
        openInXeroUrl={xeroBillUrl}
        errorMessage={pdfErrorMessage}
      />
    </div>
  )
}

export default FinancePurchases
